enum Product {
  RADAR_QA = 'RADAR_QA',
  VISION_QA = 'VISION_QA',
  SPEECH_QA = 'SPEECH_QA',
  TABULAR_QA = 'TABULAR_QA',
  TIMESERIES_QA = 'TIMESERIES_QA',
  MULTIMODAL_QA = 'MULTIMODAL_QA',
  UNLEARNING_DATA = 'UNLEARNING_DATA',
  UNLEARNING_LLM_BEHAVIOR = 'UNLEARNING_LLM_BEHAVIOR',
  UNLEARNING_LLM_DATA = 'UNLEARNING_LLM_DATA',
  LLM_BEHAVIOR_EVAL = 'LLM_BEHAVIOR_EVAL',
}

enum LlmSizeTier {
  SMALL = 'SMALL',
  MEDIUM = 'MEDIUM',
  LARGE = 'LARGE',
  XLARGE = 'XLARGE',
}

type LlmTieredProduct =
  | Product.UNLEARNING_LLM_BEHAVIOR
  | Product.UNLEARNING_LLM_DATA
  | Product.LLM_BEHAVIOR_EVAL

const unlearningProducts = [
  Product.UNLEARNING_DATA,
  Product.UNLEARNING_LLM_BEHAVIOR,
  Product.UNLEARNING_LLM_DATA,
]

type UnlearningProducts =
  | Product.UNLEARNING_DATA
  | Product.UNLEARNING_LLM_BEHAVIOR
  | Product.UNLEARNING_LLM_DATA

const PRODUCTS = [
  Product.RADAR_QA,
  Product.VISION_QA,
  Product.SPEECH_QA,
  Product.TABULAR_QA,
  Product.TIMESERIES_QA,
  Product.MULTIMODAL_QA,
  Product.UNLEARNING_DATA,
  Product.UNLEARNING_LLM_BEHAVIOR,
  Product.UNLEARNING_LLM_DATA,
  Product.LLM_BEHAVIOR_EVAL,
]

const PRODUCT_URLS = {
  [Product.RADAR_QA]: ['/data-qa'],
  [Product.VISION_QA]: ['/data-qa'],
  [Product.SPEECH_QA]: ['/data-qa'],
  [Product.TABULAR_QA]: ['/data-qa'],
  [Product.TIMESERIES_QA]: ['/data-qa'],
  [Product.MULTIMODAL_QA]: ['/data-qa'],
  [Product.UNLEARNING_DATA]: ['/unlearning-data'],
  [Product.UNLEARNING_LLM_BEHAVIOR]: ['/llm'],
  [Product.UNLEARNING_LLM_DATA]: ['/llm'],
  [Product.LLM_BEHAVIOR_EVAL]: ['/llm', '/llm/evals'],
}

const LLM_TIER_ORDER = [
  LlmSizeTier.SMALL,
  LlmSizeTier.MEDIUM,
  LlmSizeTier.LARGE,
  LlmSizeTier.XLARGE,
]

const LLM_TIER_PARAMETER_UPPER_BOUNDS = {
  [LlmSizeTier.SMALL]: 7_000_000_000,
  [LlmSizeTier.MEDIUM]: 27_000_000_000,
  [LlmSizeTier.LARGE]: 70_000_000_000,
  [LlmSizeTier.XLARGE]: Number.POSITIVE_INFINITY,
}

const LLM_TIERED_PRODUCTS: LlmTieredProduct[] = [
  Product.UNLEARNING_LLM_BEHAVIOR,
  Product.UNLEARNING_LLM_DATA,
  Product.LLM_BEHAVIOR_EVAL,
]

const isProductValue = (value: string): value is Product => PRODUCTS.includes(value as Product)

const getCanonicalProductFromEntitlement = (entitlement: string): Product | null => {
  if (isProductValue(entitlement)) {
    return entitlement
  }
  for (const llmTieredProduct of LLM_TIERED_PRODUCTS) {
    const tieredPrefix = `${llmTieredProduct}_`
    if (!entitlement.startsWith(tieredPrefix)) {
      continue
    }
    const entitlementTier = entitlement.slice(tieredPrefix.length)
    if (LLM_TIER_ORDER.includes(entitlementTier as LlmSizeTier)) {
      return llmTieredProduct
    }
  }
  return null
}

const getCanonicalActiveProducts = (activeProducts: string[] = []): Product[] => {
  const canonicalProducts = new Set<Product>()
  for (const entitlement of activeProducts) {
    const canonicalProduct = getCanonicalProductFromEntitlement(entitlement)
    if (canonicalProduct) {
      canonicalProducts.add(canonicalProduct)
      continue
    }
    console.warn(`Unable to parse product entitlement: ${entitlement}`)
  }
  return Array.from(canonicalProducts)
}

const getTierFromEntitlement = (
  entitlement: string,
  product: LlmTieredProduct,
): LlmSizeTier | null => {
  if (entitlement === product) {
    // Legacy non-tiered entitlement should remain fully permissive.
    return LlmSizeTier.XLARGE
  }
  for (const llmSizeTier of LLM_TIER_ORDER) {
    if (entitlement === `${product}_${llmSizeTier}`) {
      return llmSizeTier
    }
  }
  return null
}

const getMaxSupportedLlmTier = (
  activeProductEntitlements: string[] = [],
  product: LlmTieredProduct,
): LlmSizeTier | null => {
  let maxTierIndex = -1
  for (const activeProductEntitlement of activeProductEntitlements) {
    const tier = getTierFromEntitlement(activeProductEntitlement, product)
    if (!tier) {
      continue
    }
    const tierIndex = LLM_TIER_ORDER.indexOf(tier)
    if (tierIndex > maxTierIndex) {
      maxTierIndex = tierIndex
    }
  }
  if (maxTierIndex < 0) {
    return null
  }
  return LLM_TIER_ORDER[maxTierIndex]
}

const getLlmTierUpperBound = (tier: LlmSizeTier): number => LLM_TIER_PARAMETER_UPPER_BOUNDS[tier]

const formatParameterCountShort = (parameterCount: number): string => {
  if (parameterCount >= 1_000_000_000) {
    return `${(parameterCount / 1_000_000_000).toFixed(1)}B`
  }
  return `${(parameterCount / 1_000_000).toFixed(1)}M`
}

const getLlmTierValidationMessage = ({
  parameterCount,
  activeProductEntitlements = [],
  candidateProducts,
}: {
  parameterCount: number | null
  activeProductEntitlements?: string[]
  candidateProducts: LlmTieredProduct[]
}): string | null => {
  if (!parameterCount) {
    return null
  }

  let selectedProduct: LlmTieredProduct | null = null
  let selectedTierUpperBound = Number.NEGATIVE_INFINITY
  let selectedTierLabel: LlmSizeTier | null = null

  for (const candidateProduct of candidateProducts) {
    const maxSupportedTier = getMaxSupportedLlmTier(activeProductEntitlements, candidateProduct)
    if (!maxSupportedTier) {
      continue
    }
    const tierUpperBound = getLlmTierUpperBound(maxSupportedTier)
    if (tierUpperBound <= selectedTierUpperBound) {
      continue
    }
    selectedProduct = candidateProduct
    selectedTierUpperBound = tierUpperBound
    selectedTierLabel = maxSupportedTier
  }

  if (!selectedProduct || !selectedTierLabel) {
    return null
  }
  if (!Number.isFinite(selectedTierUpperBound)) {
    return null
  }
  if (parameterCount < selectedTierUpperBound) {
    return null
  }
  return `Model size ${formatParameterCountShort(parameterCount)} exceeds your ${selectedProduct} ${selectedTierLabel} tier (supports models below ${formatParameterCountShort(selectedTierUpperBound)}).`
}

export {
  formatParameterCountShort,
  getCanonicalActiveProducts,
  getLlmTierUpperBound,
  getLlmTierValidationMessage,
  getMaxSupportedLlmTier,
  LlmSizeTier,
  type LlmTieredProduct,
  Product,
  PRODUCT_URLS,
  PRODUCTS,
  type UnlearningProducts,
  unlearningProducts,
}
